export interface CrossTabLeaderOptions {
  channelName: string;
  leaseKey: string;
  leaseDurationMs?: number;
  heartbeatMs?: number;
  onLeaderChange: (isLeader: boolean) => void;
  onMessage?: (message: unknown) => void;
}

interface LeaderLease {
  tabId: string;
  expiresAt: number;
}

const createTabId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export class CrossTabLeader {
  private readonly tabId = createTabId();
  private readonly leaseDurationMs: number;
  private readonly heartbeatMs: number;
  private channel: BroadcastChannel | null = null;
  private timer: number | undefined;
  private leader = false;
  private started = false;
  private storageAvailable = true;

  constructor(private readonly options: CrossTabLeaderOptions) {
    this.leaseDurationMs = options.leaseDurationMs || 15000;
    this.heartbeatMs = options.heartbeatMs || 5000;
  }

  start() {
    if (this.started) return;
    this.started = true;

    if (typeof window.BroadcastChannel !== 'function') {
      this.setLeader(true);
      return;
    }

    this.channel = new window.BroadcastChannel(this.options.channelName);
    this.channel.onmessage = (event) => this.options.onMessage?.(event.data);
    window.addEventListener('storage', this.handleStorage);
    this.evaluateLeadership();
    this.timer = window.setInterval(() => this.evaluateLeadership(), this.heartbeatMs);
  }

  stop() {
    if (!this.started) return;
    this.started = false;
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = undefined;
    }
    window.removeEventListener('storage', this.handleStorage);
    this.releaseLease();
    this.channel?.close();
    this.channel = null;
    this.setLeader(false);
  }

  isLeader() {
    return this.leader;
  }

  post(message: unknown) {
    this.channel?.postMessage(message);
  }

  private readonly handleStorage = (event: StorageEvent) => {
    if (event.key === this.options.leaseKey) {
      this.evaluateLeadership();
    }
  };

  private readLease(): LeaderLease | null {
    let value: string | null;
    try {
      value = localStorage.getItem(this.options.leaseKey);
      this.storageAvailable = true;
    } catch {
      this.storageAvailable = false;
      return null;
    }
    if (!value) return null;
    try {
      const lease = JSON.parse(value) as Partial<LeaderLease>;
      if (!lease.tabId || !Number.isFinite(lease.expiresAt)) return null;
      return lease as LeaderLease;
    } catch {
      return null;
    }
  }

  private evaluateLeadership() {
    if (!this.started) return;
    const now = Date.now();
    const lease = this.readLease();
    if (!this.storageAvailable) {
      this.setLeader(true);
      return;
    }

    if (lease?.tabId === this.tabId) {
      if (!this.writeLease(now)) {
        this.setLeader(true);
        return;
      }
      this.setLeader(true);
      return;
    }

    if (!lease || lease.expiresAt <= now) {
      if (!this.writeLease(now)) {
        this.setLeader(true);
        return;
      }
      const confirmed = this.readLease();
      this.setLeader(!this.storageAvailable || confirmed?.tabId === this.tabId);
      return;
    }

    this.setLeader(false);
  }

  private writeLease(now: number) {
    try {
      localStorage.setItem(this.options.leaseKey, JSON.stringify({
        tabId: this.tabId,
        expiresAt: now + this.leaseDurationMs,
      }));
      this.storageAvailable = true;
      return true;
    } catch {
      this.storageAvailable = false;
      return false;
    }
  }

  private releaseLease() {
    try {
      const lease = this.readLease();
      if (lease?.tabId === this.tabId) {
        localStorage.removeItem(this.options.leaseKey);
        this.channel?.postMessage({ type: 'leader_released' });
      }
    } catch {
      // Storage can be unavailable in strict privacy modes.
    }
  }

  private setLeader(next: boolean) {
    if (this.leader === next) return;
    this.leader = next;
    this.options.onLeaderChange(next);
  }
}
